import { useCallback, useEffect, useRef, useState } from 'react';

import { downloadFile } from '../../pages/documents/templates/utils/download-file.util';
import {
  downloadPublicCapTable,
  fetchPublicCapTable,
  PublicCapTableError,
  PublicCapTableRequestError,
  unlockPublicCapTable,
} from './public-cap-table.api';
import { PublicCapTablePayload } from './shared-cap-table.types';

export enum PublicCapTableStatus {
  GONE = 'gone',
  LOADING = 'loading',
  PASSWORD_REQUIRED = 'password-required',
  READY = 'ready',
  /** The view budget for this link is spent; it is not broken and needs no password, only a wait. */
  THROTTLED = 'throttled',
  UNKNOWN_ERROR = 'unknown-error',
}

type PublicCapTableState = {
  /** Known before the password is accepted, so the gate can present the company. */
  companyLogoUrl: null | string;
  companyName: null | string;
  payload: null | PublicCapTablePayload;
  status: PublicCapTableStatus;
  /** Seconds left on the throttle window, when the last attempt was rejected as too frequent. */
  throttledForSeconds: null | number;
  unlockFailed: boolean;
};

/**
 * `onGate` differs by where the 429 arrives. On the first load the link may have no password at
 * all, so showing a password form would ask the viewer for a secret that does not exist; they get
 * a wait screen instead. Once at the gate, a throttled attempt keeps them there with a countdown.
 */
const statusFromError = (
  error: unknown,
  { onGate }: { onGate: boolean } = { onGate: false },
): PublicCapTableStatus => {
  if (error instanceof PublicCapTableRequestError) {
    if (error.kind === PublicCapTableError.PASSWORD_REQUIRED) {
      return PublicCapTableStatus.PASSWORD_REQUIRED;
    }

    if (error.kind === PublicCapTableError.THROTTLED) {
      return onGate ? PublicCapTableStatus.PASSWORD_REQUIRED : PublicCapTableStatus.THROTTLED;
    }

    if (error.kind === PublicCapTableError.GONE) {
      return PublicCapTableStatus.GONE;
    }
  }

  return PublicCapTableStatus.UNKNOWN_ERROR;
};

/**
 * Also read off a 401: a gate re-opened during a lockout carries the remaining block, so the form
 * starts disabled with a countdown instead of spending an attempt to discover it.
 */
/**
 * The backend numbers anonymised rows so the page and the export agree on who "Stakeholder 3" is.
 * On screen the number carries nothing — every row is equally unnamed — so it is dropped here,
 * once, rather than in each renderer. The export keeps its numbering.
 */
const withUnnumberedNames = (payload: PublicCapTablePayload): PublicCapTablePayload =>
  payload.hideStakeholderNames
    ? {
        ...payload,
        stakeholders: payload.stakeholders.map((stakeholder) => ({
          ...stakeholder,
          name: stakeholder.name.replace(/\s+\d+$/, ''),
        })),
      }
    : payload;

/** Below this a return to the tab cannot have missed anything worth a round trip. */
const MIN_REFRESH_INTERVAL_MS = 30_000;

const throttledSecondsFrom = (error: unknown): null | number =>
  error instanceof PublicCapTableRequestError &&
  (error.kind === PublicCapTableError.THROTTLED ||
    error.kind === PublicCapTableError.PASSWORD_REQUIRED)
    ? (error.retryAfterSeconds ?? null)
    : null;

/**
 * Not a React Query hook on purpose: this runs outside the authenticated app shell, where the
 * shared query client and its auth-aware retry behaviour do not apply.
 */
export const usePublicCapTable = (linkId: string | undefined) => {
  const [state, setState] = useState<PublicCapTableState>({
    companyLogoUrl: null,
    companyName: null,
    payload: null,
    status: PublicCapTableStatus.LOADING,
    throttledForSeconds: null,
    unlockFailed: false,
  });
  const [isUnlocking, setUnlocking] = useState(false);
  const lastFetchedAtRef = useRef(0);

  useEffect(() => {
    if (!linkId) {
      setState((prev) => ({ ...prev, status: PublicCapTableStatus.GONE }));

      return;
    }

    let isActive = true;

    setState((prev) => ({ ...prev, status: PublicCapTableStatus.LOADING }));
    lastFetchedAtRef.current = Date.now();

    fetchPublicCapTable(linkId)
      .then((raw) => {
        if (!isActive) return;

        const payload = withUnnumberedNames(raw);

        setState({
          companyLogoUrl: payload.companyLogoUrl,
          companyName: payload.companyName,
          payload,
          status: PublicCapTableStatus.READY,
          throttledForSeconds: null,
          unlockFailed: false,
        });
      })
      .catch((error: unknown) => {
        if (!isActive) return;

        setState({
          companyLogoUrl:
            error instanceof PublicCapTableRequestError ? (error.companyLogoUrl ?? null) : null,
          companyName:
            error instanceof PublicCapTableRequestError ? (error.companyName ?? null) : null,
          payload: null,
          status: statusFromError(error),
          throttledForSeconds: throttledSecondsFrom(error),
          unlockFailed: false,
        });
      });

    return () => {
      isActive = false;
    };
  }, [linkId]);

  /**
   * Refreshed when the viewer comes back rather than on a timer: the server answers a repeat out of
   * a version-fenced cache, so a return costs a read and only recomputes when the cap table actually
   * changed. Both events are needed — `visibilitychange` for switching tabs, `focus` for returning
   * to the window.
   *
   * The status is left alone so the table does not blink through a loader, and a failed refresh is
   * ignored: a dropped connection must not blank a table someone is reading.
   */
  useEffect(() => {
    if (!linkId || state.status !== PublicCapTableStatus.READY) return;

    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchedAtRef.current < MIN_REFRESH_INTERVAL_MS) return;

      lastFetchedAtRef.current = Date.now();

      fetchPublicCapTable(linkId)
        .then((raw) => setState((prev) => ({ ...prev, payload: withUnnumberedNames(raw) })))
        .catch(() => undefined);
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [linkId, state.status]);

  const unlock = useCallback(
    async (password: string) => {
      if (!linkId) return;

      setUnlocking(true);

      try {
        lastFetchedAtRef.current = Date.now();

        const payload = withUnnumberedNames(await unlockPublicCapTable(linkId, password));

        setState({
          companyLogoUrl: payload.companyLogoUrl,
          companyName: payload.companyName,
          payload,
          status: PublicCapTableStatus.READY,
          throttledForSeconds: null,
          unlockFailed: false,
        });
      } catch (error: unknown) {
        setState((prev) => ({
          // Keep what the first request resolved — a wrong attempt must not blank the header.
          companyLogoUrl: prev.companyLogoUrl,
          companyName: prev.companyName,
          payload: null,
          status: statusFromError(error, { onGate: true }),
          throttledForSeconds: throttledSecondsFrom(error),
          unlockFailed: true,
        }));
      } finally {
        setUnlocking(false);
      }
    },
    [linkId],
  );

  const [isExporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<null | PublicCapTableError>(null);

  /**
   * The page renders outside the authenticated shell and so has no toast host: the failure is
   * returned as state for the view to show rather than swallowed.
   */
  const exportCapTable = useCallback(
    async (password?: string) => {
      if (!linkId) return;

      setExporting(true);
      setExportError(null);

      try {
        const { blob, fileName } = await downloadPublicCapTable(linkId, password);

        downloadFile(new File([blob], fileName, { type: blob.type }));
      } catch (error: unknown) {
        setExportError(
          error instanceof PublicCapTableRequestError ? error.kind : PublicCapTableError.UNKNOWN,
        );
      } finally {
        setExporting(false);
      }
    },
    [linkId],
  );

  return { ...state, exportCapTable, exportError, isExporting, isUnlocking, unlock };
};
